import { selectLivePhrases } from '@src/videoTranscriptionProcessor/translation/selectLivePhrases';

import type { TranslationStore } from './TranslationStore';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';

/**
 * Wire the captions producer's translation phrase resolver onto `editor`
 * for the editor's entire lifetime.
 *
 * The resolver is pure: it reads the shared `TranslationStore` on each call
 * (not at wiring time) and returns a language's phrases, or `null`. `null`
 * covers every case that must fall back to original-language segmentation —
 * `'original'`, the source language (e.g. `'en-US'`, which is never a key in
 * `translations`), and any not-yet-generated language — so
 * `ProduceKaraokeCommand.getTranslationPhrases` chooses `produceOriginalClips`
 * vs `produceTranslatedClips` correctly with the resolver always attached.
 *
 * PLAT-10945 — the resolver hands back only the phrases that are still LIVE.
 * A correction retires the phrases it replaces (`active: false`) instead of
 * deleting them, and the producer resolves two phrases claiming the same source
 * word by array position, so a retired record could take those words back and
 * put its stale wording on screen. The filter lives here, at the single
 * injection point, rather than inside `ProduceKaraokeCommand`, so one rule
 * covers every caller of the producer. The store keeps the retired records —
 * they are what makes an override reversible.
 *
 * Because the resolver depends only on the singleton store — whose reference
 * never changes for the app's lifetime — a single wiring at editor
 * construction is sufficient. There is nothing to re-attach or tear down, so
 * this is called once per editor (story-level in `RootStore`, clip-scoped
 * wherever a `ClipPreviewEditorStore` is created) rather than juggled through
 * React component lifecycles. The domain editor stays store-agnostic: the
 * dependency is injected here, in the store layer.
 */
export function wireTranslationPhraseResolver(
  editor: IVideoEditor,
  translationStore: TranslationStore,
): void {
  editor.setTranslationPhraseResolver(language => {
    const data = translationStore.getTranslation(language);
    return data ? selectLivePhrases(data.phrases) : null;
  });
}
